import { Breakpoints } from '@volkswagen-onehub/components-core';
import { useEffect, useState } from 'react';

export function getCurrentBreakpoint(): Breakpoints {
	const width = window.innerWidth;

	if (width >= Breakpoints.b2560) return Breakpoints.b2560;
	if (width >= Breakpoints.b1920) return Breakpoints.b1920;
	if (width >= Breakpoints.b1600) return Breakpoints.b1600;
	if (width >= Breakpoints.b1280) return Breakpoints.b1280;
	if (width >= Breakpoints.b960) return Breakpoints.b960;
	if (width >= Breakpoints.b560) return Breakpoints.b560;

	return Breakpoints.default;
}

export function useBreakpoint(): Breakpoints {
	const [currentBreakpoint, setCurrentBreakpoint] = useState(getCurrentBreakpoint());

	useEffect(() => {
		const handleResize = () => setCurrentBreakpoint(getCurrentBreakpoint());
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return currentBreakpoint;
}

// Numeric breakpoint methods
export function is560(): boolean {
	const bp = getCurrentBreakpoint();
	return bp >= Breakpoints.b560 && bp < Breakpoints.b960;
}

export function is960(): boolean {
	const bp = getCurrentBreakpoint();
	return bp >= Breakpoints.b960 && bp < Breakpoints.b1280;
}

export function is1280(): boolean {
	const bp = getCurrentBreakpoint();
	return bp >= Breakpoints.b1280 && bp < Breakpoints.b1600;
}

export function is1600(): boolean {
	const bp = getCurrentBreakpoint();
	return bp >= Breakpoints.b1600 && bp < Breakpoints.b1920;
}

export function is1920(): boolean {
	const bp = getCurrentBreakpoint();
	return bp >= Breakpoints.b1920 && bp < Breakpoints.b2560;
}

export function is2560(): boolean {
	return getCurrentBreakpoint() >= Breakpoints.b2560;
}

export function isLargerThan560(): boolean {
	return getCurrentBreakpoint() >= Breakpoints.b560;
}

export function isLargerThan960(): boolean {
	return getCurrentBreakpoint() >= Breakpoints.b960;
}

export function isLargerThan1280(): boolean {
	return getCurrentBreakpoint() >= Breakpoints.b1280;
}

export function isLargerThan1600(): boolean {
	return getCurrentBreakpoint() >= Breakpoints.b1600;
}

export function isLargerThan1920(): boolean {
	return getCurrentBreakpoint() >= Breakpoints.b1920;
}

export function isSmallerThan560(): boolean {
	return getCurrentBreakpoint() < Breakpoints.b560;
}

export function isSmallerThan960(): boolean {
	return getCurrentBreakpoint() < Breakpoints.b960;
}

export function isSmallerThan1280(): boolean {
	return getCurrentBreakpoint() < Breakpoints.b1280;
}

export function isSmallerThan1600(): boolean {
	return getCurrentBreakpoint() < Breakpoints.b1600;
}

export function isSmallerThan1920(): boolean {
	return getCurrentBreakpoint() < Breakpoints.b1920;
}

export function isSmallerThan2560(): boolean {
	return getCurrentBreakpoint() < Breakpoints.b2560;
}

// Semantic methods using numeric ones
export function isMobile(): boolean {
	return isSmallerThan560();
}

export function isSmallTablet(): boolean {
	return is560();
}

export function isTablet(): boolean {
	return is960();
}

export function isLaptop(): boolean {
	return is1280();
}

export function isDesktop(): boolean {
	return is1600();
}

export function isLargeDesktop(): boolean {
	return is1920();
}

export function isUltraWide(): boolean {
	return is2560();
}

// Comparative semantic methods
export function isLargerThanMobile(): boolean {
	return isLargerThan560();
}

export function isLargerThanSmallTablet(): boolean {
	return isLargerThan560();
}

export function isLargerThanTablet(): boolean {
	return isLargerThan960();
}

export function isLargerThanLaptop(): boolean {
	return isLargerThan1280();
}

export function isLargerThanDesktop(): boolean {
	return isLargerThan1600();
}

export function isLargerThanLargeDesktop(): boolean {
	return isLargerThan1920();
}

export function isSmallerThanSmallTablet(): boolean {
	return isSmallerThan560();
}

export function isSmallerThanTablet(): boolean {
	return isSmallerThan960();
}

export function isSmallerThanLaptop(): boolean {
	return isSmallerThan1280();
}

export function isSmallerThanDesktop(): boolean {
	return isSmallerThan1600();
}

export function isSmallerThanLargeDesktop(): boolean {
	return isSmallerThan1920();
}

export function isSmallerThanUltraWide(): boolean {
	return isSmallerThan2560();
}
